import { useState, type FormEvent } from 'react';
import { Button, DialogActions, DialogContent, TextField } from '@mui/material';
import type { Settings } from '../../types';
import { SETTINGS_TAB_LABELS as LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface SettingsModalProps {
  settings: Settings;
  onClose: () => void;
  onSave: (values: Pick<Settings, 'default_start_time' | 'default_end_time'>) => Promise<boolean>;
}

export default function SettingsModal({ settings, onClose, onSave }: SettingsModalProps) {
  useModalHistory(onClose);
  const [startTime, setStartTime] = useState(settings.default_start_time);
  const [endTime, setEndTime] = useState(settings.default_end_time);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onSave({ default_start_time: startTime, default_end_time: endTime });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={LABELS.bookingTimesModalTitle} confirmLabel={LABELS.saveButton} confirmFormId="settings-form" confirmDisabled={saving} confirmLoading={saving} testId="settings-modal">
      <form id="settings-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={LABELS.startTimeLabel}
              type="time"
              required
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
            />
            <TextField
              label={LABELS.endTimeLabel}
              type="time"
              required
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">{LABELS.saveButton}</SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
